import React, { useCallback, useEffect, useState } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSearch } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, confirmDelete, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import BulkActions from '../../components/common/BulkActions';
import Modal from '../../components/common/Modal';
import { useAuth } from '../../context/AuthContext';

export default function Units() {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', short_name: '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.inventory.units();
      const list = Array.isArray(data) ? data : data.units || [];
      setRows(list);
    } catch (err) {
      showError('Failed to load units', extractError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openModal = (row = null) => {
    setEditing(row);
    setForm(row ? { name: row.name, short_name: row.short_name } : { name: '', short_name: '' });
    setErrors({});
    setModal(true);
  };

  const handleSave = async () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.short_name.trim()) errs.short_name = 'Short name is required';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    try {
      if (editing) {
        await api.inventory.updateUnit(editing.id, form);
        toast('success', '', 'Unit updated');
      } else {
        await api.inventory.createUnit(form);
        toast('success', '', 'Unit created');
      }
      setModal(false);
      load();
    } catch (err) {
      const msg = extractError(err);
      if (msg.includes('unique') || msg.includes('already')) {
        setErrors({ name: 'Name already exists' });
      } else {
        showError('Failed to save unit', msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const res = await confirmDelete(`unit "${row.name}"`);
    if (!res.isConfirmed) return;
    try {
      await api.inventory.deleteUnit(row.id);
      toast('success', '', 'Unit deleted');
      load();
    } catch (err) {
      showError('Failed to delete unit', extractError(err));
    }
  };

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.name || '').toLowerCase().includes(s) || (r.short_name || '').toLowerCase().includes(s);
  });

  return (
    <div className="page">
      <PageHeader
        title="Units"
        subtitle="Manage measurement units for inventory items."
        crumbs={[{ label: 'Inventory' }, { label: 'Units' }]}
        actions={hasPermission('create-units') && (
          <button className="btn btn-primary" onClick={() => openModal()}><FiPlus /> Add Unit</button>
        )}
      />

      <div className="card">
        <div className="card-body">
          <div className="filters-bar">
            <div className="topbar-search">
              <FiSearch className="search-icon" />
              <input
                placeholder="Search units..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <BulkActions selectedIds={selectedIds} onClear={() => setSelectedIds([])} onDone={load} deleteType="units" label="units" />
          <DataTable
            loading={loading}
            data={filtered}
            emptyTitle="No units found"
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            columns={[
              { key: 'name', label: 'Unit Name', render: (r) => <b>{r.name}</b> },
              { key: 'short_name', label: 'Short Name' },
              { key: 'items', label: 'Items Using', render: (r) => r.items_count !== undefined ? r.items_count : '-' },
              { key: 'id', label: '', align: 'right', render: (r) => (
                <div className="actions">
                  {hasPermission('update-units') && <button className="btn-icon" onClick={() => openModal(r)}><FiEdit2 /></button>}
                  {hasPermission('delete-units') && <button className="btn-icon danger" onClick={() => handleDelete(r)}><FiTrash2 /></button>}
                </div>
              ) },
            ]}
          />
        </div>
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={editing ? 'Edit Unit' : 'Add Unit'}
        footer={<>
          <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={handleSave}>{saving ? 'Saving...' : 'Save'}</button>
        </>}
      >
        <div className="form-group">
          <label className="form-label">Name <span className="req">*</span></label>
          <input className={`form-control ${errors.name ? 'is-invalid' : ''}`} value={form.name} onChange={(e) => { setForm({ ...form, name: e.target.value }); if (errors.name) setErrors((p) => ({ ...p, name: '' })); }} placeholder="e.g. Kilogram" />
          {errors.name && <div className="invalid-feedback">{errors.name}</div>}
        </div>
        <div className="form-group">
          <label className="form-label">Short Name <span className="req">*</span></label>
          <input className={`form-control ${errors.short_name ? 'is-invalid' : ''}`} value={form.short_name} onChange={(e) => { setForm({ ...form, short_name: e.target.value }); if (errors.short_name) setErrors((p) => ({ ...p, short_name: '' })); }} placeholder="e.g. kg" />
          {errors.short_name && <div className="invalid-feedback">{errors.short_name}</div>}
        </div>
      </Modal>
    </div>
  );
}
